import { useEffect, useMemo, useState } from "react";
import { prefixCssWithId } from "@forms/form-render/components/_common/prefixCssWithId";
import { Button } from "@shadcn/components/ui/button";
import { Loader2 } from "lucide-react";
import { getContentStepContent } from "@/data/journeyContent/api";
import { useUserJourneyContextOptional } from "@/context/UserJourneyContext";
import { TurnstileActionFeedback } from "../../_common/TurnstileActionFeedback";
import { ContentStepProps } from "./ContentStep.types";

export const ContentStep = ({
  id,
  journeyId,
  version,
  hideNextButton,
  onSubmit,
  initialHtml = "",
  initialCss = "",
  renderBeforeActions,
  disableNextUntilTurnstile,
  turnstileFeedback,
}: ContentStepProps) => {
  const journeyContext = useUserJourneyContextOptional();
  const effectiveJourneyId = useMemo(() => journeyId ?? journeyContext?.journey?.id, [journeyId, journeyContext?.journey?.id]);
  const effectiveVersion = useMemo(() => version ?? journeyContext?.journeyVersion ?? "dev", [version, journeyContext?.journeyVersion]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [html, setHtml] = useState(initialHtml ?? "");
  const [css, setCss] = useState(initialCss ?? "");
  const [loading, setLoading] = useState(!(initialHtml || initialCss));
  useEffect(() => {
    const hasInitialContent = Boolean(initialHtml) || Boolean(initialCss);
    if (initialHtml !== undefined && initialHtml !== null) {
      setHtml(initialHtml);
    }
    if (initialCss !== undefined && initialCss !== null) {
      setCss(initialCss);
    }
    if (hasInitialContent) {
      setLoading(false);
    }
  }, [initialHtml, initialCss]);

  useEffect(() => {
    if (!effectiveJourneyId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    getContentStepContent(effectiveJourneyId, id, effectiveVersion).then((content) => {
      if (cancelled) return;
      if (content) {
        setHtml(content.html ?? "");
        setCss(content.css ?? "");
      }
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [effectiveJourneyId, effectiveVersion, id]);

  hideNextButton = !!hideNextButton;
  const scopedCss = css ? prefixCssWithId(css, id) : undefined;

  const onNextClicked = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(id, {});
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <>
      {scopedCss && <style id={`${id}-css`}>{scopedCss}</style>}
      <div className="space-y-6">
        <div id={`${id}`} dangerouslySetInnerHTML={{ __html: html }} />
        {!hideNextButton && (
          <>
            {renderBeforeActions && renderBeforeActions}
            {turnstileFeedback ? (
              <TurnstileActionFeedback className="mt-4" {...turnstileFeedback} />
            ) : null}
            <div className="mt-4 flex justify-center">
              <Button
                type="submit"
                variant="default"
                disabled={isSubmitting || !!disableNextUntilTurnstile}
                onClick={onNextClicked}
                size="lg"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Please wait
                  </>
                ) : (
                  "Next"
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
};
